// src/components/lesson/diagrams/FunctionGraph.tsx
//
// Plots one or more functions on a small coordinate grid. Used inline in lesson
// markdown via [[diagram:<id>]] markers and by the tutor via fenced ```diagram
// blocks.
//
// Supported function `kind`s:
//   - linear:      y = slope·x + intercept
//   - quadratic:   y = a·x² + b·x + c
//   - exponential: y = a·base^x + c           (Nonlinear Functions)
//   - cubic:       y = a·x³ + b·x² + c·x + d   (Nonlinear Functions)
//   - absolute:    y = a·|x − h| + k           (absolute-value functions)
//
// Extras:
//   - curves: [{ kind, ...params, color? }]   overlay MORE functions (systems
//             of equations → two lines + an intersection point)
//   - shade:  "above" | "below"               shade the region relative to the
//             PRIMARY curve (linear inequalities / feasible regions)
//   - points / highlightX / showRiseRun       point markers + slope rise/run
//     (unchanged — existing lessons keep working)
//
// Params shape (primary function is read from the top-level fields; additional
// functions go in `curves`):
//   { "kind": "linear", "slope": -2, "intercept": 8,
//     "xMin": -2, "xMax": 8, "yMin": -2, "yMax": 12,
//     "curves": [{ "kind": "linear", "slope": 1, "intercept": 0 }],
//     "points": [{ "x": 4, "y": 4, "label": "(4, 4)" }],   // intersection
//     "shade": "below" }

import {
  evalFn,
  formatNum,
  graphWindow,
  overlayCurves,
  resolveHighlightPoints,
  riseRunAnnotation,
  type FnSpec,
  type FunctionGraphParams,
} from '@/lib/render/function-graph-model'

const W = 360
const H = 240
const PAD = 28
const PRIMARY = 'rgb(34 211 238)' // cyan
const OVERLAY_COLORS = ['rgb(167 139 250)', 'rgb(52 211 153)', 'rgb(251 113 133)'] // violet/emerald/rose

export default function FunctionGraph({ params }: { params: Record<string, unknown> }) {
  const p = params as FunctionGraphParams
  // The drawn window (incl. the vertex auto-framing) lives in the shared model
  // so the screen-reader description describes exactly this picture.
  const { xMin, xMax, yMin, yMax } = graphWindow(p)

  const xToPx = (x: number) => PAD + ((x - xMin) / (xMax - xMin)) * (W - 2 * PAD)
  const yToPx = (y: number) => H - PAD - ((y - yMin) / (yMax - yMin)) * (H - 2 * PAD)

  const N = 100
  /** Sample a spec into on-canvas {x,y} px points within the y window. */
  const sample = (spec: FnSpec): { x: number; y: number }[] => {
    const out: { x: number; y: number }[] = []
    for (let i = 0; i <= N; i++) {
      const x = xMin + ((xMax - xMin) * i) / N
      const y = evalFn(spec, x)
      if (Number.isFinite(y) && y >= yMin - 200 && y <= yMax + 200) {
        out.push({ x: xToPx(x), y: yToPx(y) })
      }
    }
    return out
  }
  const toPath = (pts: { x: number; y: number }[]) =>
    pts.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`).join(' ')

  const primaryPts = sample(p)
  const primaryPath = toPath(primaryPts)
  const overlays = overlayCurves(p).map((spec, i) => ({
    path: toPath(sample(spec)),
    color: spec.color || OVERLAY_COLORS[i % OVERLAY_COLORS.length],
  }))

  // Shaded region: close the primary curve against the top (above) or bottom
  // (below) edge of the plot area.
  let shadePath: string | null = null
  if ((p.shade === 'above' || p.shade === 'below') && primaryPts.length > 1) {
    const edgeY = p.shade === 'above' ? PAD : H - PAD
    const first = primaryPts[0]
    const last = primaryPts[primaryPts.length - 1]
    shadePath = `${toPath(primaryPts)} L ${last.x.toFixed(1)} ${edgeY} L ${first.x.toFixed(1)} ${edgeY} Z`
  }

  const highlightPoints = resolveHighlightPoints(p)
  const riseRun = riseRunAnnotation(p)

  const xTicks: number[] = []
  for (let x = Math.ceil(xMin); x <= Math.floor(xMax); x++) xTicks.push(x)
  const yTicks: number[] = []
  for (let y = Math.ceil(yMin); y <= Math.floor(yMax); y++) yTicks.push(y)
  const x0 = xToPx(0)
  const y0 = yToPx(0)

  // Number the axes. A graph asking "what is f(0)?" is unanswerable without
  // scale labels, so print the value at each tick — thinning to a readable
  // subset when the range is wide, and skipping 0 (the origin already shows it).
  const labelStep = (count: number) => (count <= 8 ? 1 : count <= 16 ? 2 : count <= 40 ? 5 : 10)
  const xStep = labelStep(xTicks.length)
  const yStep = labelStep(yTicks.length)
  // Pin the number rows to the axes when they're on-canvas, else to the edge so
  // the labels never fall outside the plot.
  const xAxisY = Math.min(Math.max(y0, PAD), H - PAD)
  const yAxisX = Math.min(Math.max(x0, PAD), W - PAD)
  const AXIS_LABEL = 'rgb(100 116 139)' // slate-500 — same tone as the origin "0"

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Function graph'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Grid */}
      {xTicks.map((x) => (
        <line
          key={`vx-${x}`}
          x1={xToPx(x)}
          x2={xToPx(x)}
          y1={PAD}
          y2={H - PAD}
          stroke="rgb(30 41 59 / 0.8)"
          strokeWidth={x === 0 ? 1.5 : 0.5}
        />
      ))}
      {yTicks.map((y) => (
        <line
          key={`hy-${y}`}
          x1={PAD}
          x2={W - PAD}
          y1={yToPx(y)}
          y2={yToPx(y)}
          stroke="rgb(30 41 59 / 0.8)"
          strokeWidth={y === 0 ? 1.5 : 0.5}
        />
      ))}

      {xMin <= 0 && xMax >= 0 && yMin <= 0 && yMax >= 0 && (
        <text x={x0 + 4} y={y0 - 4} fontSize="10" fill={AXIS_LABEL}>
          0
        </text>
      )}

      {/* x-axis tick numbers (below the axis; 0 is covered by the origin) */}
      {xTicks
        .filter((x) => x !== 0 && x % xStep === 0)
        .map((x) => (
          <text
            key={`xl-${x}`}
            x={xToPx(x)}
            y={xAxisY + 12}
            fontSize="9"
            textAnchor="middle"
            fill={AXIS_LABEL}
          >
            {x}
          </text>
        ))}
      {/* y-axis tick numbers (left of the axis) */}
      {yTicks
        .filter((y) => y !== 0 && y % yStep === 0)
        .map((y) => (
          <text
            key={`yl-${y}`}
            x={yAxisX - 5}
            y={yToPx(y) + 3}
            fontSize="9"
            textAnchor="end"
            fill={AXIS_LABEL}
          >
            {y}
          </text>
        ))}

      {/* Shaded region (under the curves so the curve outline stays crisp) */}
      {shadePath && <path d={shadePath} fill="rgb(34 211 238 / 0.12)" stroke="none" />}

      {/* Overlay curves (systems) */}
      {overlays.map((o, i) => (
        <path key={`ov-${i}`} d={o.path} stroke={o.color} strokeWidth="2" fill="none" />
      ))}

      {/* Primary curve */}
      <path d={primaryPath} stroke={PRIMARY} strokeWidth="2" fill="none" />

      {/* Rise/run annotation */}
      {riseRun && (
        <>
          <line
            x1={xToPx(highlightPoints[0].x)}
            y1={yToPx(highlightPoints[0].y)}
            x2={xToPx(highlightPoints[1].x)}
            y2={yToPx(highlightPoints[0].y)}
            stroke="rgb(251 191 36)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <line
            x1={xToPx(highlightPoints[1].x)}
            y1={yToPx(highlightPoints[0].y)}
            x2={xToPx(highlightPoints[1].x)}
            y2={yToPx(highlightPoints[1].y)}
            stroke="rgb(251 191 36)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <text
            x={(xToPx(highlightPoints[0].x) + xToPx(highlightPoints[1].x)) / 2}
            y={yToPx(highlightPoints[0].y) + 14}
            fontSize="11"
            fontWeight="600"
            fill="rgb(251 191 36)"
            textAnchor="middle"
          >
            run = {formatNum(riseRun.run)}
          </text>
          <text
            x={xToPx(highlightPoints[1].x) + 6}
            y={(yToPx(highlightPoints[0].y) + yToPx(highlightPoints[1].y)) / 2 + 4}
            fontSize="11"
            fontWeight="600"
            fill="rgb(251 191 36)"
          >
            rise = {formatNum(riseRun.rise)}
          </text>
        </>
      )}

      {/* Highlighted points (intersections, marked coordinates) */}
      {highlightPoints.map((pt, i) => {
        if (pt.x < xMin || pt.x > xMax || pt.y < yMin || pt.y > yMax) return null
        const cx = xToPx(pt.x)
        const cy = yToPx(pt.y)
        return (
          <g key={`pt-${i}`}>
            <circle
              cx={cx}
              cy={cy}
              r="5"
              fill="rgb(251 191 36)"
              stroke="rgb(15 23 42)"
              strokeWidth="2"
            />
            {pt.label && (
              <text x={cx + 8} y={cy - 8} fontSize="11" fontWeight="600" fill="rgb(251 191 36)">
                {pt.label}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}
